import { Injectable, inject } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import type {
  CreateValuationRequest,
  IncomeApproachInput,
  IncomeApproachResponse,
  PropertyFactsResponse,
  SaveUnitsRequest,
  TaxBillResponse,
  ValuationListResponse,
  ValuationSessionResponse,
} from '../models/valuation.models';
import type {
  ComparablesResponse,
  UpdateComparableSelectionRequest,
} from '../models/valuation-comparable.models';
import { ApiClientService } from './api-client.service';

@Injectable({
  providedIn: 'root',
})
export class ValuationService {
  private readonly apiClient = inject(ApiClientService);

  /** No current callers -- kept for parity with the backend endpoint. Return
   * type follows GET /recent's actual shape (ValuationListResponse, same as
   * listValuations -- the backend no longer has a separate "recent" shape). */
  getRecentValuations(limit = 5): Observable<ValuationListResponse> {
    return this.apiClient.get<ValuationListResponse>(
      `/valuation/recent?limit=${limit}`,
    );
  }

  listValuations(limit = 200): Observable<ValuationListResponse> {
    return this.apiClient.get<ValuationListResponse>(`/valuation?limit=${limit}`);
  }

  createValuation(apn: string, appealYear: number): Observable<ValuationSessionResponse> {
    const body: CreateValuationRequest = { apn, appeal_year: appealYear };
    return this.apiClient.post<ValuationSessionResponse, CreateValuationRequest>(
      '/valuation',
      body,
    );
  }

  getSession(sessionId: string): Observable<ValuationSessionResponse> {
    return this.apiClient.get<ValuationSessionResponse>(`/valuation/${sessionId}`);
  }

  deleteSession(sessionId: string): Observable<void> {
    return this.apiClient.delete<void>(`/valuation/${sessionId}`);
  }

  getPropertyFacts(sessionId: string): Observable<PropertyFactsResponse> {
    return this.apiClient.get<PropertyFactsResponse>(`/valuation/${sessionId}/facts`);
  }

  saveUnits(sessionId: string, body: SaveUnitsRequest): Observable<PropertyFactsResponse> {
    return this.apiClient.put<PropertyFactsResponse, SaveUnitsRequest>(
      `/valuation/${sessionId}/units`,
      body,
    );
  }

  getPropertyMap(sessionId: string): Observable<Blob> {
    return this.apiClient.getBlob(`/valuation/${sessionId}/map`);
  }

  refreshPropertyMap(sessionId: string): Observable<Blob> {
    return this.apiClient.postForBlob(`/valuation/${sessionId}/map/refresh`);
  }

  getIncomeApproach(sessionId: string): Observable<IncomeApproachResponse> {
    return this.apiClient.get<IncomeApproachResponse>(
      `/valuation/${sessionId}/income-approach`,
    );
  }

  saveIncomeApproach(
    sessionId: string,
    input: IncomeApproachInput,
  ): Observable<IncomeApproachResponse> {
    return this.apiClient.put<IncomeApproachResponse, IncomeApproachInput>(
      `/valuation/${sessionId}/income-approach`,
      input,
    );
  }

  getTaxBill(sessionId: string): Observable<TaxBillResponse> {
    return this.apiClient.get<TaxBillResponse>(`/valuation/${sessionId}/tax-bill`);
  }

  getTaxBillImage(sessionId: string): Observable<Blob> {
    return this.apiClient.getBlob(`/valuation/${sessionId}/tax-bill/image`);
  }

  getTaxBillSource(sessionId: string): Observable<Blob> {
    return this.apiClient.getBlob(`/valuation/${sessionId}/tax-bill/source`);
  }

  refreshTaxBill(sessionId: string): Observable<TaxBillResponse> {
    return this.apiClient.post<TaxBillResponse>(`/valuation/${sessionId}/tax-bill/refresh`);
  }

  saveTaxBill(sessionId: string, taxRate: number): Observable<TaxBillResponse> {
    return this.apiClient.put<TaxBillResponse>(`/valuation/${sessionId}/tax-bill`, {
      tax_rate: taxRate,
    });
  }

  uploadTaxBill(sessionId: string, formData: FormData): Observable<TaxBillResponse> {
    return this.apiClient.postFormData<TaxBillResponse>(
      `/valuation/${sessionId}/tax-bill/upload`,
      formData,
    );
  }

  downloadValuation(caseId: string): Observable<HttpResponse<Blob>> {
    return this.apiClient.getBlobResponse(`/valuation/${caseId}/download`);
  }

  downloadBatchValuation(caseId: string): Observable<Blob> {
    return this.apiClient.getBlob(`/valuation/batch-generate/${caseId}/download`);
  }

  getComparables(sessionId: string): Observable<ComparablesResponse> {
    return this.apiClient.get<ComparablesResponse>(`/valuation/${sessionId}/comparables`);
  }

  refreshComparables(sessionId: string): Observable<ComparablesResponse> {
    return this.apiClient.post<ComparablesResponse>(
      `/valuation/${sessionId}/comparables/refresh`,
    );
  }

  updateComparableSelection(
    sessionId: string,
    body: UpdateComparableSelectionRequest,
  ): Observable<ComparablesResponse> {
    return this.apiClient.put<ComparablesResponse, UpdateComparableSelectionRequest>(
      `/valuation/${sessionId}/comparables/selection`,
      body,
    );
  }
}
